import { AppState, type AppStateStatus, type NativeEventSubscription } from 'react-native';

import { BUDGET_WS_URL } from '../config/environment';
import type { ImportWsEvent } from '../types/import';

export type ImportWsConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

export type ImportWebSocketSessionOptions = {
  accessToken: string;
  /** Called for every server event (including pong / subscribed). */
  onEvent: (event: ImportWsEvent) => void;
  onStateChange?: (state: ImportWsConnectionState) => void;
  /** Fired after an unexpected close while a batch is still subscribed. */
  onUnexpectedClose?: () => void;
};

const PING_INTERVAL_MS = 25_000;
const RECONNECT_BASE_MS = 800;
const RECONNECT_MAX_MS = 8_000;
const RECONNECT_MAX_ATTEMPTS = 5;

/**
 * Lifecycle-aware import WebSocket:
 * - Connect + subscribe while an import is active and the app is foregrounded
 * - Ping keepalive while open
 * - Close on background (OS will drop the socket anyway)
 * - Reconnect + resubscribe on foreground / unexpected close
 */
export class ImportWebSocketSession {
  private socket: WebSocket | null = null;
  private state: ImportWsConnectionState = 'idle';
  private importBatchId: string | null = null;
  private intentionallyClosed = false;
  private appState: AppStateStatus = AppState.currentState;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private appStateSub: NativeEventSubscription | null = null;
  private readonly options: ImportWebSocketSessionOptions;

  constructor(options: ImportWebSocketSessionOptions) {
    this.options = options;
    this.appStateSub = AppState.addEventListener('change', this.handleAppState);
  }

  get connectionState(): ImportWsConnectionState {
    return this.state;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /** Begin (or replace) subscription for a batch. Opens the socket if needed. */
  subscribe(importBatchId: string): void {
    this.importBatchId = importBatchId;
    this.intentionallyClosed = false;
    this.reconnectAttempts = 0;

    if (this.appState !== 'active') {
      // Stay subscribed in memory; connect when we return to foreground.
      return;
    }

    this.openSocket();
  }

  /** Tear down permanently (import finished / modal dismissed). */
  dispose(): void {
    this.intentionallyClosed = true;
    this.importBatchId = null;
    this.clearReconnect();
    this.clearPing();
    this.appStateSub?.remove();
    this.appStateSub = null;
    this.closeSocket();
    this.setState('idle');
  }

  private setState(next: ImportWsConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.options.onStateChange?.(next);
  }

  private handleAppState = (next: AppStateStatus): void => {
    const prev = this.appState;
    this.appState = next;

    if (next === 'active' && prev.match(/inactive|background/)) {
      if (this.importBatchId && !this.intentionallyClosed) {
        this.reconnectAttempts = 0;
        this.openSocket();
      }
      return;
    }

    if (next.match(/inactive|background/) && prev === 'active') {
      // Drop the socket while backgrounded — API Gateway idle timeout + OS kill it anyway.
      // Keep importBatchId so we resubscribe on foreground.
      this.clearReconnect();
      this.closeSocket();
    }
  };

  private openSocket(): void {
    if (!BUDGET_WS_URL) {
      console.warn('[ImportWebSocketSession] BUDGET_WS_URL is not set');
      return;
    }
    if (!this.importBatchId || this.intentionallyClosed) return;
    if (this.appState !== 'active') return;

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      if (this.socket.readyState === WebSocket.OPEN && this.importBatchId) {
        this.sendSubscribe(this.importBatchId);
      }
      return;
    }

    this.clearReconnect();
    this.closeSocket();
    this.setState('connecting');

    const params = new URLSearchParams({ token: this.options.accessToken });
    if (this.importBatchId) {
      params.set('importBatchId', this.importBatchId);
    }

    const url = `${BUDGET_WS_URL}?${params.toString()}`;
    console.info('[ImportWS] 🔌 Connecting…', {
      importBatchId: this.importBatchId,
      wsHost: BUDGET_WS_URL.replace(/\?.*$/, ''),
    });
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket || this.intentionallyClosed) return;
      this.reconnectAttempts = 0;
      this.setState('open');
      this.startPing();
      console.info('[ImportWS] ✅ Socket open', { importBatchId: this.importBatchId });
      if (this.importBatchId) {
        this.sendSubscribe(this.importBatchId);
      }
    };

    socket.onmessage = (message) => {
      if (this.socket !== socket) return;
      try {
        const data = JSON.parse(String(message.data)) as ImportWsEvent;
        if (data && typeof data === 'object' && 'event' in data) {
          const eventName = data.event;
          if (eventName === 'pong') {
            console.debug('[ImportWS] 🏓 pong');
          } else if (eventName === 'subscribed') {
            console.info('[ImportWS] 📡 subscribed', {
              importBatchId: 'importBatchId' in data ? data.importBatchId : undefined,
            });
          } else if (eventName === 'image_parsed') {
            console.info('[ImportWS] 📥 image_parsed', {
              importBatchId: data.importBatchId,
              imageIndex: data.imageIndex,
              inserted: data.inserted,
              duplicateCount: data.duplicateCount,
              runningTransactionCount: data.runningTransactionCount,
              merchants: data.transactions?.slice(0, 5).map((t) => t.merchant),
            });
          } else if (eventName === 'import_complete') {
            console.info('[ImportWS] 🎉 import_complete', {
              importBatchId: data.importBatchId,
              transactionCount: data.transactionCount,
              duplicateCount: data.duplicateCount,
              processedImages: data.processedImages,
              totalImages: data.totalImages,
              failedFiles: data.fileErrors?.length ?? 0,
            });
          } else if (eventName === 'import_failed' || eventName === 'image_failed') {
            console.warn('[ImportWS] ❌', eventName, data);
          } else {
            console.info('[ImportWS] 📥', eventName, data);
          }
          this.options.onEvent(data);
        } else {
          console.warn('[ImportWS] Unexpected frame shape', message.data);
        }
      } catch (error) {
        console.warn('[ImportWS] Failed to parse frame', { error, raw: message.data });
      }
    };

    socket.onerror = () => {
      console.warn('[ImportWS] ⚠️ Socket error', { importBatchId: this.importBatchId });
    };

    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearPing();
      this.setState('closed');
      console.info('[ImportWS] 🔌 Socket closed', {
        importBatchId: this.importBatchId,
        intentional: this.intentionallyClosed,
      });

      if (this.intentionallyClosed || !this.importBatchId) return;

      this.options.onUnexpectedClose?.();

      if (this.appState === 'active') {
        this.scheduleReconnect();
      }
    };
  }

  private sendSubscribe(importBatchId: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    console.info('[ImportWS] 📤 subscribe', { importBatchId });
    this.socket.send(JSON.stringify({ action: 'subscribe', importBatchId }));
  }

  private startPing(): void {
    this.clearPing();
    this.pingTimer = setInterval(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      try {
        this.socket.send(JSON.stringify({ action: 'ping' }));
      } catch {
        // ignore
      }
    }, PING_INTERVAL_MS);
  }

  private clearPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionallyClosed || !this.importBatchId) return;
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.warn('[ImportWebSocketSession] Max reconnect attempts reached');
      return;
    }

    this.clearReconnect();
    const attempt = this.reconnectAttempts;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.intentionallyClosed || this.appState !== 'active' || !this.importBatchId) return;
      console.info('[ImportWebSocketSession] Reconnecting…', { attempt: attempt + 1 });
      this.openSocket();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeSocket(): void {
    this.clearPing();
    if (!this.socket) return;
    const socket = this.socket;
    this.socket = null;
    try {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    } catch {
      // ignore
    }
  }
}

export function isImportWebSocketConfigured(): boolean {
  return Boolean(BUDGET_WS_URL);
}
